/**
 * React hook for managing crop schedule grouping state
 */

import { useState, useMemo, useCallback } from 'react'
import { groupCropsByStrategy } from '../utils/crop-grouping-utils'

/**
 * Hook for managing crop schedule groups with expand/collapse state
 *
 * @param {Object} params
 * @param {Array} params.crops - Array of crop objects to group
 * @param {string} params.groupStrategy - Grouping strategy ('none' | 'species' | 'location' | 'family')
 * @returns {Object} { groups, expandedGroupIds, toggleGroup, expandGroup, collapseGroup }
 */
export const useCropScheduleGroups = ({ crops, groupStrategy = 'none' }) => {
  const [expandedGroupIds, setExpandedGroupIds] = useState([])

  // Memoize groups calculation to avoid re-computing on every render
  const groups = useMemo(() => {
    return groupCropsByStrategy(crops, groupStrategy)
  }, [crops, groupStrategy])

  // Toggle a group's expansion state
  // When expanding, also expand all individual crops within that group
  const toggleGroup = useCallback((groupId) => {
    setExpandedGroupIds(prev => {
      if (prev.includes(groupId)) {
        // Collapsing - remove group from expanded list
        return prev.filter(id => id !== groupId)
      } else {
        // Expanding - add group to expanded list
        return [...prev, groupId]
      }
    })
  }, [])

  // Expand a specific group
  const expandGroup = useCallback((groupId) => {
    setExpandedGroupIds(prev => {
      if (!prev.includes(groupId)) {
        return [...prev, groupId]
      }
      return prev
    })
  }, [])

  // Collapse a specific group
  const collapseGroup = useCallback((groupId) => {
    setExpandedGroupIds(prev => prev.filter(id => id !== groupId))
  }, [])

  // Collapse all groups
  const collapseAllGroups = useCallback(() => {
    setExpandedGroupIds([])
  }, [])

  // Expand all groups
  const expandAllGroups = useCallback(() => {
    setExpandedGroupIds(groups.map(g => g.groupId))
  }, [groups])

  return {
    groups,
    expandedGroupIds,
    toggleGroup,
    expandGroup,
    collapseGroup,
    collapseAllGroups,
    expandAllGroups
  }
}
